import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useMemo } from 'react';

import { ExtendedTextNode } from './ExtendedTextNode';
import { ThemeProperties } from '@mutiny-pkg/champaign-schemas/src/schemas/themeProperties/types';
import { MentionNode } from '@mutiny-pkg/bin-ui/src/richtext/MentionsPlugin/MentionNode';
import { LexicalThemeColors } from '../types';

const normalizeColor = (color: string | undefined): string | undefined => {
  if (!color) return color;

  const srgbMatch = color.match(/^color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+))?\)$/);
  if (srgbMatch) {
    const [, r, g, b, alpha] = srgbMatch;
    const rVal = Math.round(Number(r) * 255);
    const gVal = Math.round(Number(g) * 255);
    const bVal = Math.round(Number(b) * 255);

    if (alpha && Number(alpha) !== 1) {
      return `rgba(${rVal}, ${gVal}, ${bVal}, ${alpha})`;
    }
    return `rgb(${rVal}, ${gVal}, ${bVal})`;
  }

  return color;
};

const resolveColor = (color: string | undefined, theme: ThemeProperties) => {
  let resolvedColor = color;
  if (color && color in theme.color) {
    resolvedColor = theme.color[color as keyof typeof theme.color];
  }
  return normalizeColor(resolvedColor);
};

export function resolveThemeValues(node: ExtendedTextNode, theme: ThemeProperties) {
  // Resolve theme colors here
  const resolvedHighlightColor = resolveColor(node.getHighlightColor(), theme);
  if (node.getResolvedHighlightColor() !== resolvedHighlightColor) {
    node.setResolvedHighlightColor(resolvedHighlightColor);
  }

  const resolvedFontColor = resolveColor(node.getFontColor(), theme);
  if (node.getResolvedFontColor() !== resolvedFontColor) {
    node.setResolvedFontColor(resolvedFontColor);
  }
}

export function ExtendedTextPlugin({
  noDisplayColor,
  themeColors,
}: {
  noDisplayColor: boolean;
  themeColors?: LexicalThemeColors;
}) {
  const [editor] = useLexicalComposerContext();
  const theme: ThemeProperties = useMemo(
    () =>
      ({
        color: themeColors?.color ?? ({} as ThemeProperties['color']),
        additionalColors: themeColors?.additionalColors ?? [],
      } as ThemeProperties),
    [themeColors],
  );

  // In order to stay in sync with the current theme colors, we store a reference
  // to the theme color in the database. We need to resolve that theme color here, so
  // we can assign the actual color as a style on the node.
  useEffect(() => {
    return editor.registerNodeTransform(ExtendedTextNode, (node) => {
      resolveThemeValues(node, theme);
      if (node.getNoDisplayColor() !== noDisplayColor) {
        node.setNoDisplayColor(noDisplayColor);
      }
    });
  }, [editor, noDisplayColor, theme]);

  useEffect(() => {
    return editor.registerNodeTransform(MentionNode, (node) => {
      resolveThemeValues(node, theme);
      if (node.getNoDisplayColor() !== noDisplayColor) {
        node.setNoDisplayColor(noDisplayColor);
      }
    });
  }, [editor, noDisplayColor, theme]);

  return undefined;
}
